import {
  del, get, post, put,
} from './API';
import { messageChannel } from '../Utils/messageChannel';

/**
 * The office's shared library of saved passenger messages — the chips in the
 * passenger-message composer, and the screen that manages them.
 *
 * ONE service for ONE endpoint, `/message-presets`, which multiplexes all
 * four methods onto a single Lambda (every API resource adds rights to the
 * CI execution user's IAM policy, and that policy is close to its limit).
 * The management screen and the composer both come through here: a second
 * client for "the same list, but read-only" is exactly the kind of pair that
 * drifts.
 *
 * TWO READS, AND THE DIFFERENCE MATTERS.
 *
 *   `fetchPresets()`              — the bodies AS STORED, with `{pickup}`
 *                                   and friends still in them. This is what
 *                                   the management screen edits.
 *   `fetchPresetsForBooking(id)`  — the same list with every variable
 *                                   FILLED IN from that booking. This is
 *                                   what a composer shows.
 *
 * The substitution is the server's, not ours, and that is deliberate: the
 * free-waiting allowances in a message like "for airport we have 60 minutes"
 * are a sales channel's commercial terms, resolved from the booking's
 * partner. A Booking.com job quotes Booking.com's minutes. The browser has
 * no way to know them and must not guess — so the booking-scoped read is the
 * only way a composer gets a sendable body.
 *
 * It also answers with `omitted`: the presets that CANNOT be sent against
 * this booking (a variable it cannot fill, or a body that no longer fits
 * once filled), each with a reason in plain words. A composer shows those as
 * a note rather than letting a chip silently vanish.
 *
 * `variables` rides along on both reads — the vocabulary and its help text,
 * straight from the server, so the management screen explains what the API
 * actually supports instead of a second copy of the list that goes stale.
 *
 * EVERY ROW CARRIES A CHANNEL, and an email one carries a SUBJECT too. The
 * endpoint can also filter a read (`?channel=whatsapp|email`), but nothing
 * here asks it to: the management screen edits the whole library, and the
 * composers filter in the browser so a channel toggle swaps the chip row
 * without a second round trip (`Utils/messageChannel`). A row with no
 * `channel` is a WhatsApp one — the rows that predate the field were all
 * written for the WhatsApp template, and the API agrees from its side.
 */

const payloadOf = (response) => response?.data?.data || response?.data || {};

const MessagePresetsService = {
  /** The library as stored: raw bodies. `{ presets, variables }`. */
  fetchPresets: async () => {
    const payload = payloadOf(await get('/message-presets'));
    return { presets: payload.presets || [], variables: payload.variables || [] };
  },

  /**
   * The library resolved against one booking: sendable bodies, in chip
   * order. `{ presets, omitted, variables }`.
   */
  fetchPresetsForBooking: async (bookingId) => {
    const payload = payloadOf(await get('/message-presets', { booking_id: bookingId }));
    return {
      presets: payload.presets || [],
      omitted: payload.omitted || [],
      variables: payload.variables || [],
    };
  },

  /**
   * Add a chip. New ones land at the end of the row.
   *
   * `channel` and `subject` are ALWAYS sent, including `subject: ''` on a
   * WhatsApp row, so a row can never be left half-switched between the two
   * shapes — an email subject stranded on a WhatsApp snippet would be
   * invisible in every composer and still there on the next edit.
   */
  createPreset: async ({
    label, body, channel, subject,
  }) => payloadOf(await post('/message-presets', {
    label,
    body,
    // Normalised to a real value rather than left out: a row this screen
    // creates says which channel it is for, instead of leaning on the
    // server's default for a field the form always knows.
    channel: messageChannel(channel),
    subject: subject || '',
  })),

  /**
   * Change a chip's words, its channel or its place in the row. ANY SUBSET —
   * `sort_order` on its own is how reordering is saved, and a reorder must
   * not have an opinion about the channel of the row it is moving.
   */
  updatePreset: async ({
    id, label, body, channel, subject, sortOrder,
  }) => payloadOf(await put('/message-presets', {
    id,
    ...(label === undefined ? {} : { label }),
    ...(body === undefined ? {} : { body }),
    ...(channel === undefined ? {} : { channel }),
    ...(subject === undefined ? {} : { subject }),
    ...(sortOrder === undefined ? {} : { sort_order: sortOrder }),
  })),

  /** Remove a chip. SOFT on the server (`active: false`), so it is recoverable. */
  deletePreset: async (id) => payloadOf(await del('/message-presets', { id })),
};

export default MessagePresetsService;
